import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { adminApi, fetchAdminMe } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

const AdminLogin = () => {
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();
    const [err, setErr] = useState(null);
    const [checking, setChecking] = useState(true);

    // If already logged in, skip the form and go to /admin.
    useEffect(() => {
        let cancelled = false;
        fetchAdminMe().then((admin) => {
            if (cancelled) return;
            if (admin) navigate("/admin", { replace: true });
            else setChecking(false);
        });
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const onSubmit = async (data) => {
        setErr(null);
        try {
            await adminApi.post("/admin/login", data);
            toast.success("Welcome back.");
            navigate("/admin", { replace: true });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Invalid credentials.";
            setErr(msg);
        }
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo_brand">
                <Loader2 className="animate-spin text-white" size={26} />
            </div>
        );
    }

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen flex items-center justify-center bg-indigo_brand relative overflow-hidden p-6"
        >
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_30%_20%,#08C020_0%,transparent_45%),radial-gradient(circle_at_70%_80%,#C9A646_0%,transparent_45%)]" />
            <div className="relative w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <Logo variant="light" />
                </div>
                <div className="ih-card p-10 shadow-medium bg-white">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={18} className="text-green_brand" />
                        <p className="ih-eyebrow">Insights Center</p>
                    </div>
                    <h1 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl tracking-tight">Sign in</h1>
                    <p className="mt-2 font-opensans text-[#4A5568] text-sm">Authorized personnel only.</p>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="mt-7 space-y-5"
                        data-testid="admin-login-form"
                    >
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">Email</Label>
                            <Input
                                type="email"
                                autoFocus
                                data-testid="admin-login-email"
                                {...register("email", { required: true })}
                                placeholder="info@theintuitushub.com"
                                className="mt-2 h-12"
                            />
                            {errors.email && <p className="mt-1 text-xs text-red-600">Email is required.</p>}
                        </div>
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">Password</Label>
                            <Input
                                type="password"
                                data-testid="admin-login-password"
                                {...register("password", { required: true })}
                                placeholder="••••••••"
                                className="mt-2 h-12"
                            />
                            {errors.password && <p className="mt-1 text-xs text-red-600">Password is required.</p>}
                        </div>
                        {err && (
                            <p
                                data-testid="admin-login-error"
                                className="font-opensans text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
                            >
                                {err}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="admin-login-submit"
                            className="ih-btn-primary w-full justify-center disabled:opacity-60"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin" size={16} /> Signing in…
                                </>
                            ) : (
                                <>
                                    Sign in to Insights Center <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </form>
                </div>
                <p className="mt-6 text-center font-opensans text-xs text-white/55">© The Intuitus Hub™</p>
            </div>
        </div>
    );
};

export default AdminLogin;
